import { styled } from '@mui/material/styles';
import {
  Button,
  Popover,
  MenuItem,
  Box,
  Typography,
  PopoverProps,
} from '@mui/material';
import { actionMenuPopoverPosition } from 'src/utils/properties.utils';
import { FC } from 'react';
import TextField from 'src/components/InputFields/TextField';
import { TextFieldProps } from 'src/models/FieldProps.interface';

export const ActionMenuButton = styled(Button)({
  fontSize: '16px',
  color: 'var(--border-blue)',
  minWidth: 'auto',
  padding: 0,
}) as typeof Button;

export const ActionMenuButtonWithDisabled = styled(Button)<{
  isEditingDisabled?: boolean;
}>(({ isEditingDisabled }) => ({
  fontSize: '16px',
  color: isEditingDisabled ? 'var(--border)' : 'var(--border-blue)',
  pointerEvents: isEditingDisabled ? 'none' : 'auto',
  minWidth: 'auto',
  padding: 0,
})) as typeof Button;

const StyledPopover = styled(Popover)<{ width?: string }>(({ width }) => ({
  '& .MuiPopover-paper': {
    width: width || '190px',
    backgroundColor: 'var(--bg-color)',
    boxShadow: '0px 4px 6px var(--box-shadow)',
  },
}));

export const ActionMenuPopover = (props: PopoverProps & { width?: string }) => (
  <StyledPopover
    anchorOrigin={actionMenuPopoverPosition.anchorOrigin}
    transformOrigin={actionMenuPopoverPosition.transformOrigin}
    {...props}
  />
);

export const ActionMenuItem = styled(MenuItem)({
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'var(--inactive-btn)',
    '& .MuiTypography-root': {
      color: 'var(--primary-blue)',
    },
    '& .bx': {
      color: 'var(--primary-blue)',
    },
  },
});

export const MenuItemBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '40px',
  width: '100%',
  borderRadius: '8px',
});

export const MenuItemText = styled(Typography)({
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: 'Outfit',
  color: 'var(--primary-navy)',
});

export const MenuItemsWrapper = styled(Box)({
  padding: '8px',
});

export const MenuIcon = styled('i')({
  marginRight: '8px',
  fontSize: '16px',
});

export const EllipsisText40 = styled(Typography)({
  width: '40%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
});

export const EllipsisText40Instructions = styled(Typography)({
  width: '40%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: 'var(--instructions)',
});

export const EllipsisText40PrimaryNavy = styled(Typography)({
  width: '40%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: 'var(--primary-navy)',
});

export const EllipsisText30 = styled(Typography)({
  width: '30%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
});

export const EllipsisText30Instructions = styled(Typography)({
  width: '30%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
  textAlign: 'right',
  color: 'var(--instructions)',
});

export const EllipsisText30PrimaryNavy = styled(Typography)({
  width: '30%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
  textAlign: 'right',
  color: 'var(--primary-navy)',
  paddingRight: '12px',
});

export const BoxItemTextField = styled(Box)({
  width: '30%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
});

export const RightAlignedTextField: FC<TextFieldProps> = (props) => (
  <TextField
    {...props}
    sx={{
      '& .MuiOutlinedInput-input': {
        textAlign: 'right',
        padding: '8px 12px',
      },
      ...props.sx,
    }}
  />
);

export const ClickableBox = styled(Box)({
  cursor: 'pointer',
});

export const SaveButton = styled(Button)({
  backgroundColor: 'var(--primary-blue)',
  textTransform: 'none',
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: '400',
  color: 'var(--bg-color)',
  border: '1px solid var(--primary-blue)',
  borderRadius: '8px',
  width: '80px',
  '&:hover': {
    backgroundColor: 'var(--primary-blue)',
    filter: 'brightness(0.85)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--primary-blue-disabled)',
    color: 'var(--bg-color)',
    border: '1px solid var(--inactive-border)',
    borderRadius: '8px',
    textTransform: 'none',
    opacity: 1,
    cursor: 'not-allowed',
  },
});

export const CancelButton = styled(Button)({
  width: '100px',
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: '500',
  color: 'var(--error)',
  borderColor: 'var(--error)',
  borderRadius: '8px',
});

export const MinusIconBox = styled(Box)({
  fontSize: '18px',
  color: 'var(--instructions)',
  cursor: 'pointer',
});

export const PlusIconBox = styled(Box)({
  fontSize: '24px',
  color: 'var(--primary-blue)',
  marginRight: '4px',
});

export const EllipsisText = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const EllipsisTextPrimaryNavy = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: 'var(--primary-navy)',
});

export const BoxWithMarginBottom = styled(Box)({
  marginBottom: '24px',
});

export const Box300Width = styled(Box)({
  width: '300px',
});

interface ItemsContainerBoxProps {
  height?: string;
  width?: string;
  maxHeight?: string;
  noScroll?: boolean;
}

export const ItemsContainerBox = styled(Box)<ItemsContainerBoxProps>(
  ({ height = '130px', width = '595px', maxHeight, noScroll = false }) => ({
    marginTop: '8px',
    padding: '16px',
    width,
    border: '1px solid var(--border)',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
  })
);

export const ItemsHeaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: '16px',
  fontFamily: 'Outfit',
  fontWeight: '500',
  fontSize: '14px',
  color: 'var(--primary-navy)',
});

export const ItemRowBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: '8px',
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
});

export const SectionTitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-navy)',
});

export const SectionTitleTypographyWithMargin = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-navy)',
  marginBottom: '16px',
});

export const FormSectionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '16px',
});

export const FormRowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginBottom: '32px',
});

export const FormColumnBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginRight: '40px',
});

export const ItemDetailsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
});

export const QuantityInputContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

export const RemoveItemContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  marginLeft: '16px',
  marginBottom: '32px',
});

export const ItemNameTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: 'var(--primary-navy)',
});

export const InstructionsTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: 'var(--instructions)',
});

export const AddItemTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-blue)',
});

export const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  marginTop: '24px',
  marginBottom: '16px',
});

export const ButtonContainerBottom = styled(Box)({
  display: 'flex',
  gap: '16px',
  marginTop: 'auto',
  paddingTop: '24px',
  paddingBottom: '16px',
});

interface ScrollableContentBoxProps {
  height?: string;
  width?: string;
}

export const ScrollableContentBox = styled(Box)<ScrollableContentBoxProps>(
  ({ height = '300px', width = '100%' }) => ({
    width,
    height,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  })
);

export const AddItemContainer = styled(Box)({
  width: '100px',
  display: 'flex',
  marginTop: '16px',
  marginBottom: '16px',
  cursor: 'pointer',
});

export const BoxWithMarginBottom2 = styled(Box)({
  marginBottom: '16px',
  width: '100%',
});

export const FlexColumnBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const FormFieldRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 40,
});

interface MenuItemWithIconProps {
  onClick: () => void;
  iconClassName?: string;
  icon?: React.ReactNode;
  text: string;
}

export const MenuItemWithIcon: FC<MenuItemWithIconProps> = ({
  onClick,
  iconClassName,
  icon,
  text,
}) => (
  <ActionMenuItem onClick={onClick}>
    <MenuItemBox>
      {icon ? (
        <Box sx={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      ) : (
        <MenuIcon className={iconClassName || ''} />
      )}
      <MenuItemText>{text}</MenuItemText>
    </MenuItemBox>
  </ActionMenuItem>
);
